/**
 *  Implements a library to manage mutually exclusive active DOM elements
 *
 * - Only one element can be active at a given time
 * - Activating a new element, deactivates existing ones
 * - Interacting anywhere else on the document deactivates all elements
 * - Interacting with the element itself does not cause any deactivation
 * - Optional: elements can be removed instead of deactivated
 *
 * Makes use of the following "data"-Attributes:
 *     data-closable: Defines a closable and contains its name
 *     data-closable-close: Closes the current closable if set to true (false has no effect). E.g. button inside of a closable should close the closable [optional; default: false]
 *     data-closable-active: Indicates whether a closable is active ("true") or not ("false"). Must be present on a closable
 *     data-closable-remove: Indicates whether a closable should be removed ("true") instead of set inactive [optional; default: false]
 *     data-closable-target: Defines an element to toggle a closable and contains the name of the closable to control
 *     data-closable-register-events: Indicates whether a [data-closable-target] element gets event listeners for toggle() [optional; default: true]
 *     data-closable-target-focus: Defines a elementquery that should be focused when the given closable is closed
 */

import { Logger } from '@/lib/log.ts';

const log = Logger('header.closable');

const triggers = new Set();
const closables = new Set();

let initialized = false;
let activeClosable: HTMLElement | null;
let informElem: HTMLElement;

/**
 * Find closable target for trigger
 * @param root
 * @param trigger
 */
function addClosables(root, trigger): void {
	if (!trigger?.dataset?.closableTarget) {
		return;
	}

	const target =
		Boolean(root) && Boolean(trigger) && root.querySelector(`[data-closable=${trigger.dataset.closableTarget}]`);

	if (target) {
		closables.add(target);
	}
}

/**
 * @param root
 * @returns
 */
function addTriggersAndClosables(root): void {
	if (root.dataset?.closableTarget) {
		attachClickHandler(root);
		addClosables(root.getRootNode(), root);
		triggers.add(root);
	} else {
		const nodes = root.querySelectorAll('[data-closable-target]');

		nodes.forEach((node) => {
			attachClickHandler(node);
			addClosables(root, node);
			triggers.add(node);
		});
	}
}

/**
 * Attach click handler to trigger node
 * @param node
 * @returns
 */
function attachClickHandler(node): void {
	node.removeEventListener('click', closableClickhandler);
	node.addEventListener('click', closableClickhandler);
}

/**
 * Changes closable state to new state and changes focus if the optional parameter
 * 'focusableClosable' is available.
 * @param closable
 * @param newState
 * @param focusableClosable [optional] closable with data-closable-target-focus attribute
 * @returns
 */
function changeState(closable: HTMLElement, newState: boolean, focusableClosable?: HTMLElement): void {
	if (newState) {
		activeClosable = open(closable);
	} else {
		activeClosable = close(closable);
	}

	// this will only work if the element is already in DOM!
	if (focusableClosable) {
		focusableClosable.focus();
	}
}

/**
 * @param event clickevent
 */
function closableClickhandler(event) {
	event.preventDefault();
	event.stopPropagation();
	toggle(event.target);
}

/**
 * Close active closable
 * @param closable
 * @returns
 */
function close(closable: HTMLElement): null {
	if (!closable) {
		return null;
	}

	log.debug(`closing closable ${closable.dataset.closable}`);
	closable.dataset.closableActive = 'false';

	if (informElem) {
		informElem.removeAttribute('data-active-closable');
	}

	const event = new CustomEvent('dst.closable.close', {
		detail: {
			closable: HTMLElement,
		},
	});

	window.dispatchEvent(event);

	const remove = closable.dataset.closableRemove === 'true';

	if (remove) {
		removeClosableFromDOM(closable);
	}

	return null;
}

/**
 * Initialize
 */
function initialize(): void {
	log.debug('Initializing closable script');
	initialized = true;
	document.addEventListener('click', onDocumentClick);
	document.addEventListener('keyup', onPressEscape);
}

/**
 * Handles clicks on the document to deactivate closables
 * @param event
 * @returns
 */
function onDocumentClick(event: Event): void {
	const eventPath: EventTarget[] = event.composedPath();
	const isTrigger = eventPath.find((item) => (item as HTMLElement).dataset?.closableTarget) || null;
	const isWithinClosable = eventPath.find((item) => item === activeClosable);

	if (!isTrigger && !isWithinClosable) {
		activeClosable = close(activeClosable);
	}
}

/**
 * Close active closable with ESC Key
 * @param event
 * @returns
 */
function onPressEscape(event: KeyboardEvent): void {
	if (event.key === 'Escape') {
		const focusSelector =
			activeClosable?.dataset?.closableTargetFocus ||
			(activeClosable?.querySelector('[data-closable-target]') as HTMLElement)?.dataset?.closableTargetFocus;

		if (focusSelector) {
			(document.querySelector(focusSelector) as HTMLElement)?.focus();
		}

		activeClosable = close(activeClosable);
	}
}

/**
 * Open new closable
 * @param closable
 * @returns closable
 */
function open(closable: HTMLElement): HTMLElement {
	activeClosable = close(activeClosable);
	log.debug(`opening closable ${closable.dataset.closable}`);
	closable.dataset.closableActive = 'true';

	if (informElem) {
		informElem.setAttribute('data-active-closable', 'true');
	}

	return closable;
}

/**
 * Add a collection of triggers to the trigger registry
 * @param root
 * @param inform
 * @returns
 */
function register(root: Document | DocumentFragment | HTMLElement, inform?: HTMLElement): void {
	if (!root) {
		return;
	}
	if (!initialized) {
		initialize();
	}
	informElem = inform;
	log.debug(`Registering from root ${root}`);
	addTriggersAndClosables(root);
	log.debug(`Registered a total of ${triggers.size} and triggers ${closables.size} closables`);
}

/**
 * Remove closable from Set and DOM
 * @param closable
 * @returns
 */
function removeClosableFromDOM(closable: HTMLElement): void {
	const closableInDOM = (closable.getRootNode() as HTMLDocument | ShadowRoot).querySelector(
		`[data-closable="${closable.dataset.closable}"]`,
	);

	closableInDOM.parentNode.removeChild(closableInDOM);
	closables.delete(closable);
}

/**
 * Checks if a closable has the dataattribute 'data-closable-target-focus'
 * and changes the focus to the given selector of the dataattribute
 * @param closable
 * @returns
 */
function setFocus(closable: HTMLElement): void {
	const focusableSelector = closable.dataset.closableTargetFocus;

	if (focusableSelector) {
		(document.querySelector(focusableSelector) as HTMLElement)?.focus();
	}
}

/**
 * Open closable
 * @param eventTarget
 * @returns
 */
function toggle(eventTarget: HTMLElement): void {
	// check for closable-close
	if ((eventTarget?.closest('[data-closable-target]') as HTMLElement)?.dataset?.closableClose === 'true') {
		activeClosable = close(activeClosable);
	}

	const closableName = (eventTarget.closest('[data-closable-target]') as HTMLElement).dataset.closableTarget;

	if (activeClosable?.dataset?.closable === closableName) {
		activeClosable = close(activeClosable);
	} else {
		let foundClosable = false;

		// search closable set
		closables.forEach((closable: HTMLElement) => {
			if (closable.dataset.closable === closableName) {
				activeClosable = open(closable);
				foundClosable = true;
			}
		});

		// run a documentquery if closable was not found in closable set
		if (!foundClosable) {
			const closableTarget = document.querySelector(`[data-closable="${closableName}"]`) as HTMLElement;

			if (closableTarget) {
				activeClosable = open(closableTarget);
			}
		}
	}

	// finally set the focus after closing/opening
	setFocus(eventTarget?.closest('[data-closable-target]') as HTMLElement);
}

export { changeState, register };
